import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { mobileDevices, revokeMobileDevice, type MobileDevice } from '../api/auth';
import { confirmDialog } from '../shell/dialogs';

function fmt(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function platformLabel(p: MobileDevice['platform']): string {
  return p === 'ios' ? 'iOS' : 'Android';
}

export function MobileDevicesPane() {
  const [devices, setDevices] = useState<MobileDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setDevices(await mobileDevices());
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Načtení selhalo.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(d: MobileDevice) {
    const ok = await confirmDialog({
      title: 'Odhlásit zařízení?',
      message: `Aplikace na zařízení „${d.name}“ se okamžitě odhlásí a pro další použití bude potřeba nové přihlášení.`,
      confirmLabel: 'Odhlásit',
      danger: true,
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await revokeMobileDevice(d.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Odhlášení selhalo.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="settings-form-layout settings-password-pane">
      <section className="settings-form-panel">
        <header className="settings-panel-head">
          <div>
            <span>Zabezpečení</span>
            <h2>Přihlášená zařízení</h2>
            <p>Telefony a tablety, na kterých jste přihlášení v mobilní aplikaci.</p>
          </div>
        </header>
        <div className="settings-form-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {!devices ? (
            <p className="settings-loading">Načítám…</p>
          ) : devices.length === 0 ? (
            <p className="text-muted small mb-0">Mobilní aplikace zatím není přihlášená na žádném zařízení.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Zařízení</th>
                    <th>Systém</th>
                    <th>Verze aplikace</th>
                    <th>Přihlášeno</th>
                    <th>Naposledy aktivní</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id}>
                      <td>{d.name}{d.current ? <span className="text-muted small"> (toto zařízení)</span> : null}</td>
                      <td className="small">{platformLabel(d.platform)}</td>
                      <td className="small">{d.appVersion ?? '-'}</td>
                      <td className="small">{fmt(d.createdAt)}</td>
                      <td className="small">{fmt(d.lastSeenAt)}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          style={{ textTransform: 'none' }}
                          disabled={busyId === d.id}
                          onClick={() => void revoke(d)}
                        >
                          Odhlásit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <aside className="settings-context-panel">
        <span>Jak to funguje</span>
        <h2>Bezpečné přihlášení v telefonu</h2>
        <p>Přihlášení v aplikaci vyžaduje dvoufázové ověření a platí nejvýš 30 dní, po 14 dnech bez použití vyprší. Současně můžete být přihlášení nejvýš na 5 zařízeních. Změna hesla odhlásí všechna zařízení.</p>
      </aside>
    </div>
  );
}
